// components/crm/contact/tabs/ActivityItem.tsx
import React from 'react'
import { Activity } from 'types/crm/activity'
import { ContactOwner } from 'types/crm/contacts'
import { getActivityVisual, isExpandableActivity } from './activityConfig'

const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString)
  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

// Bolds the performer name and the acted-on entity name (deal/company/etc.)
// inside the plain-text `statement` string coming from the backend.
const HighlightedStatement = ({ activity }: { activity: Activity }) => {
  const highlights = [activity.performedBy?.name, activity.actionOnName].filter(
    (v): v is string => !!v && activity?.statement.includes(v),
  )

  if (highlights.length === 0) {
    return <>{activity.statement}</>
  }

  const pattern = new RegExp(`(${highlights.map((h) => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g')
  const parts = activity?.statement.split(pattern)

  return (
    <>
      {parts.map((part, i) =>
        highlights.includes(part) ? (
          <span key={i} className="font-medium text-neutral">
            {part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  )
}

interface ActivityItemProps {
  activity: Activity
  owners: ContactOwner[]
}

const ActivityItem = ({ activity }: ActivityItemProps) => {
  const { icon: Icon, iconBg, iconColor } = getActivityVisual(activity.activityType)
  const expandable = isExpandableActivity(activity.referenceType)

  // Simple, non-expandable log line (owner changed, associations, etc.)
  if (!expandable) {
    return (
      <div className="flex items-start gap-3 py-2.5">
        <span className={`p-1.5 rounded-lg shrink-0 ${iconBg}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </span>
        <p className="flex-1 min-w-0 text-sm text-neutral-faded pt-1">
          <HighlightedStatement activity={activity} />
        </p>
        <span className="text-xs text-neutral-faded shrink-0 pt-1">
          {formatRelativeTime(activity.activityDateTime)}
        </span>
      </div>
    )
  }

  // Note / Task activity — same simple line style as other activities.
  // Once the backend attaches the full Note/CrmTask object to these
  // entries, swap this body for <NoteItem note={...} /> / <TaskItem task={...} />.
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className={`p-1.5 rounded-lg shrink-0 ${iconBg}`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-neutral whitespace-pre-wrap break-words line-clamp-1">
            {activity.activityDescription}
          </p>
          <span className="text-xs text-neutral-faded shrink-0">
            {formatRelativeTime(activity.activityDateTime)}
          </span>
        </div>

        {activity.performedBy && (
          <p className="text-xs text-neutral-faded mt-1">
            By <span className="text-neutral-faded">{activity.performedBy.name}</span>
          </p>
        )}
      </div>
    </div>
  )
}

export default ActivityItem